import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Bell, Check } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    try { const { data } = await api.get("/notifications"); setItems(data); }
    catch { /* ignore */ }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const unread = items.filter(n => !n.read).length;

  const markAll = async () => {
    try { await api.patch("/notifications/read-all"); load(); }
    catch { /* ignore */ }
  };

  const openItem = async (n) => {
    try { await api.patch(`/notifications/${n.id}/read`); } catch { /* ignore */ }
    setOpen(false);
    if (n.link) nav(n.link);
    load();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid="notification-bell" className="relative w-9 h-9 flex items-center justify-center hover:bg-gray-100 transition-colors">
          <Bell className="w-4 h-4 text-gray-700" strokeWidth={2} />
          {unread > 0 && (
            <span data-testid="notification-badge" className="absolute top-1 right-1 min-w-[16px] h-4 px-1 bg-[#DC2626] text-white text-[10px] font-bold flex items-center justify-center">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0 rounded-md border-gray-200">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <div>
            <div className="font-display font-bold tracking-tight">Notifications</div>
            <div className="text-xs text-gray-500">{unread} unread</div>
          </div>
          {unread > 0 && (
            <button data-testid="mark-all-read" onClick={markAll} className="text-xs uppercase tracking-wider font-semibold text-[#01ADEF] hover:text-[#0198D6]">Mark all read</button>
          )}
        </div>
        <div className="max-h-[400px] overflow-y-auto divide-y divide-gray-100" data-testid="notifications-list">
          {items.length === 0 ? (
            <div className="text-center py-8 text-sm text-gray-500">No notifications.</div>
          ) : items.map(n => (
            <button
              key={n.id}
              data-testid={`notification-${n.id}`}
              onClick={() => openItem(n)}
              className={`w-full text-left px-4 py-3 hover:bg-gray-50 flex items-start gap-3 ${n.read ? "" : "bg-[#F0F7FF]"}`}
            >
              <div className={`w-2 h-2 mt-1.5 shrink-0 ${n.read ? "bg-gray-300" : "bg-[#DC2626]"}`} />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-gray-900 break-words">{n.message}</div>
                <div className="text-xs text-gray-500 mt-0.5">{new Date(n.created_at).toLocaleString()}</div>
              </div>
              {n.read && <Check className="w-3.5 h-3.5 text-gray-400 mt-1" />}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
